import type { User } from "firebase/auth";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { AREA_FILL_OPACITY, darker, labelPoint, pathsOf, ringOf } from "./areaMap";
import type { GoogleMap, Maps, Marker, Polygon } from "./maps";

const FAINT_FILL = 0.05;
const FAINT_STROKE = 0.35;

export interface AreaShapes {
  count: number;
  areas: api.Area[];
  replace: (area: api.Area) => void;
  /** Fetch the areas again, for example after an import of shapes. */
  reload: () => void;
}

/** Rule V-17: draw the polygon and the code of each area that the user can see; no maps, no request. */
export function useAreaShapes(
  maps: Maps | null,
  map: RefObject<GoogleMap | null>,
  user: User,
  shown: boolean,
  { hide = null, onPick, faint = false }: { hide?: string | null; onPick?: (area: api.Area) => void; faint?: boolean } = {},
): AreaShapes {
  const [areas, setAreas] = useState<api.Area[]>([]);
  const [loads, setLoads] = useState(0);
  const pick = useRef(onPick);
  pick.current = onPick;
  const picking = onPick !== undefined;
  useEffect(() => {
    if (!maps) return;
    let live = true;
    user
      .getIdToken()
      .then(api.areas)
      .then((found) => live && setAreas(found))
      .catch(() => live && setAreas([]));
    return () => {
      live = false;
    };
  }, [user, maps, loads]);
  useEffect(() => {
    const view = map.current;
    if (!maps || !view || !shown) return;
    const drawn: { shape: Polygon; label: Marker }[] = [];
    for (const area of areas) {
      const paths = pathsOf(area.polygon);
      if (!paths.length || area.id === hide) continue;
      const shape = new maps.maps.Polygon({
        map: view,
        paths,
        strokeColor: darker(area.colour),
        strokeOpacity: faint ? FAINT_STROKE : 0.9,
        strokeWeight: faint ? 1 : 2,
        fillColor: area.colour,
        fillOpacity: faint ? FAINT_FILL : AREA_FILL_OPACITY,
        clickable: picking,
        zIndex: 0,
      });
      if (picking) shape.addListener("click", () => pick.current?.(area));
      const content = labelElement(area);
      if (faint) content.classList.add("faint");
      const label = new maps.marker.AdvancedMarkerElement({ map: view, position: labelPoint(paths[0]), title: `Area ${area.code}`, content, zIndex: 0 });
      drawn.push({ shape, label });
    }
    return () => {
      for (const { shape, label } of drawn) {
        shape.setMap(null);
        label.map = null;
      }
    };
  }, [maps, map, areas, shown, hide, picking, faint]);
  const replace = useCallback((area: api.Area) => setAreas((current) => current.map((other) => (other.id === area.id ? area : other))), []);
  const reload = useCallback(() => setLoads((count) => count + 1), []);
  return { count: areas.filter((area) => ringOf(area.polygon).length).length, areas, replace, reload };
}

function labelElement(area: api.Area): HTMLElement {
  const element = document.createElement("div");
  element.className = "area-label";
  element.style.borderColor = darker(area.colour);
  element.textContent = area.code;
  return element;
}

/** The Areas switch of a map; it is in the bar above the map on each screen size. */
export function AreaToggle({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className="area-toggle">
      <input type="checkbox" data-testid="area-toggle" checked={on} onChange={(e) => onChange(e.target.checked)} /> Areas
    </label>
  );
}
